import React, { FunctionComponent, useMemo } from 'react';
import { Button, Space, Table } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons/lib';
import { TableComponentProps } from '../../interfaces/table-component-props.interface';
import { ToolDto } from '@tek-crm/backend/dist/application/tools/dto/tool.dto';
import { useRemoveHandler } from '../../hooks/use-remove-handler';
import { remove } from '../../api/tools.api';
import { PageWrapper } from '../shared/styled';
import { useAccessRule } from '../../hooks/use-access-rule';
import { AccessAttributeDomain } from '@tek-crm/backend/dist/domain/user/types/access-attribute.type';

const useToolsColumns = ({ pagination, openTab, refresh }) => {
  const createRemoveHandler = useRemoveHandler({
    remove,
    refresh,
    description: 'Инструмент НК был успешно удалён',
  });

  const viewPermission = useAccessRule({
    domain: AccessAttributeDomain.Tool,
    requestedPermission: 'view',
  });

  const managePermission = useAccessRule({
    domain: AccessAttributeDomain.Tool,
    requestedPermission: 'manage',
  });

  return useMemo(
    () => [
      {
        title: 'Название',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: 'Тип',
        dataIndex: 'type',
        render: type => {
          switch (type) {
            case 'Purifier':
              return 'Очиститель';
            case 'Penetrant':
              return 'Пенетрант';
            case 'Developer':
              return 'Проявитель';
            case 'Device':
              return 'Прибор';
            case 'TuningSample':
              return 'СОП';
            case 'Sample':
              return 'Образец';
            case 'Transducer':
              return 'ПЭП';

            default:
              return 'Неизвестно';
          }
        },
      },
      {
        title: 'Действия',
        key: 'action',
        fixed: 'right' as const,
        width: 100,
        render: (text, record) => (
          <Space size="middle">
            <Button
              type="dashed"
              disabled={!viewPermission}
              icon={<EditOutlined />}
              onClick={() => openTab({ type: 'edit', key: record.id })}
            />
            <Button
              danger
              type="dashed"
              disabled={!managePermission}
              icon={<DeleteOutlined />}
              onClick={createRemoveHandler(record.id)}
            />
          </Space>
        ),
      },
    ],
    [pagination, openTab],
  );
};

export const ToolsTable: FunctionComponent<TableComponentProps<ToolDto>> = ({
  state,
  onTableChange,
  show,
  openTab,
  refresh,
}) => {
  const columns = useToolsColumns({
    pagination: state.pagination,
    openTab,
    refresh,
  });

  const managePermission = useAccessRule({
    domain: AccessAttributeDomain.Tool,
    requestedPermission: 'manage',
  });

  if (!show) {
    return null;
  }

  return (
    <PageWrapper>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button
          block
          type="primary"
          disabled={!managePermission}
          onClick={() => openTab({ key: 'add' })}
        >
          Добавить
        </Button>
        <Table
          scroll={{ x: 700 }}
          columns={columns}
          rowKey={record => record.id}
          dataSource={state.data}
          pagination={state.pagination}
          loading={state.loading}
          onChange={onTableChange}
        />
      </Space>
    </PageWrapper>
  );
};
